/**
 * Per-page CMS field schemas.
 *
 * The same ContentEditor renders different fields based on the selected
 * page. Each schema has:
 *   - title / description (shown at the top of the editor)
 *   - groups: array of { key, title, description, items[] }
 *   - sectionToggles?: keys exposed in the Section Controls panel
 *
 * Items support types: text | textarea | select | image | toggle
 */

import { TOK_PHILOSOPHY } from "./philosophy";

// Common SEO + Hero fields reused across all pages.
const seoGroup = (defaultTitle) => ({
  key: "seo",
  title: "SEO",
  description: "Page title + meta description used by Google, social previews and accessibility.",
  items: [
    { key: "seo.title", label: "Page title", type: "text", placeholder: defaultTitle },
    { key: "seo.description", label: "Meta description", type: "textarea" },
  ],
});

const heroGroup = ({ image = true, secondaryCta = true } = {}) => ({
  key: "hero",
  title: "Hero",
  description: "The opening section. Includes the kicker, headline, body and CTA cluster.",
  items: [
    { key: "hero.eyebrow", label: "Eyebrow / kicker", type: "text" },
    { key: "hero.title", label: "Headline", type: "text" },
    { key: "hero.copy", label: "Body paragraph", type: "textarea" },
    ...(image
      ? [{ key: "hero.image", label: "Background image", type: "image" }]
      : []),
    { key: "hero.primaryCtaLabel", label: "Primary CTA — label", type: "text" },
    { key: "hero.primaryCtaHref", label: "Primary CTA — link", type: "text" },
    ...(secondaryCta
      ? [
          { key: "hero.secondaryCtaLabel", label: "Secondary CTA — label", type: "text" },
          { key: "hero.secondaryCtaHref", label: "Secondary CTA — link", type: "text" },
        ]
      : []),
  ],
});

const philosophyLineSelect = (key, label = "Philosophy line") => ({
  key,
  label,
  type: "select",
  options: TOK_PHILOSOPHY.lines.map((l) => ({
    value: l.key,
    label: `${l.line.slice(0, 60)}${l.line.length > 60 ? "…" : ""}`,
  })),
});

// ----------------------------------------------------------------------
// HOMEPAGE schema (the rich one — preserves Phase 2 fields)
// ----------------------------------------------------------------------
const HOMEPAGE = {
  key: "homepage",
  label: "Homepage",
  hint: "Most-edited page. Section reorder available.",
  groups: [
    {
      key: "hero",
      title: "Hero",
      description: "The opening rail. The animated headline stays — editable copy below.",
      items: [
        { key: "hero.eyebrow", label: "Eyebrow / kicker", type: "text" },
        { key: "hero.subtagline", label: "Subtagline (white)", type: "text" },
        { key: "hero.subbody", label: "Body paragraph", type: "textarea" },
        { key: "hero.primaryCtaLabel", label: "Primary CTA — label", type: "text" },
        { key: "hero.primaryCtaHref", label: "Primary CTA — link", type: "text" },
        { key: "hero.secondaryCtaLabel", label: "Secondary CTA — label", type: "text" },
        { key: "hero.secondaryCtaHref", label: "Secondary CTA — link", type: "text" },
      ],
    },
    {
      key: "pyramid",
      title: "Pyramid Portal — core philosophy",
      description: "Cinematic atmospheric section anchoring TOK's north-star line.",
      items: [
        { key: "pyramid.image", label: "Background image", type: "image" },
        { key: "pyramid.eyebrow", label: "Eyebrow", type: "text" },
        { key: "philosophy.coreLine", label: "Quote", type: "textarea" },
        { key: "philosophy.coreAttribution", label: "Attribution", type: "text" },
      ],
    },
    {
      key: "midPhil",
      title: "Mid-page philosophy banner",
      description: "Cinematic line that breaks the homepage rhythm.",
      items: [philosophyLineSelect("midPhilosophyLineKey")],
    },
    {
      key: "social",
      title: "Social Ecosystem",
      description: "Follow-the-ecosystem band with the featured card and follow buttons.",
      items: [
        { key: "social.eyebrow", label: "Eyebrow", type: "text" },
        { key: "social.title", label: "Headline", type: "text" },
        { key: "social.copy", label: "Short sentence", type: "textarea" },
      ],
    },
  ],
  hasSectionControls: true, // visibility + drag-and-drop reorder
  previewPath: "/",
};

// ----------------------------------------------------------------------
// Generic Phase-3 page schema — hero + SEO + philosophy + linking
// ----------------------------------------------------------------------
function makePageSchema({
  key,
  label,
  hint,
  previewPath,
  defaultSeoTitle,
  hideHeroImage = false,
  extraGroups = [],
  visibleSectionKeys = ["hero", "philosophy", "nextSteps"],
}) {
  return {
    key,
    label,
    hint,
    previewPath,
    groups: [
      heroGroup({ image: !hideHeroImage }),
      seoGroup(defaultSeoTitle),
      {
        key: "philosophy",
        title: "Philosophy moment",
        description: "Optional TOK philosophy quote shown on this page.",
        items: [
          philosophyLineSelect("philosophyLineKey", "Line"),
          {
            key: "philosophyVisible",
            label: "Show on page",
            type: "toggle",
          },
        ],
      },
      {
        key: "linking",
        title: "Next-steps linking",
        description: "Where this page sends visitors next. Up to 3 destinations.",
        items: [
          { key: "next.1.label", label: "Link 1 — label", type: "text" },
          { key: "next.1.href", label: "Link 1 — destination", type: "text" },
          { key: "next.2.label", label: "Link 2 — label", type: "text" },
          { key: "next.2.href", label: "Link 2 — destination", type: "text" },
          { key: "next.3.label", label: "Link 3 — label", type: "text" },
          { key: "next.3.href", label: "Link 3 — destination", type: "text" },
        ],
      },
      ...extraGroups,
    ],
    hasSectionControls: true,
    sectionTogglesOnly: true,
    visibleSectionKeys,
  };
}

const PHASE3 = [
  makePageSchema({
    key: "experiences",
    label: "Experiences",
    hint: "Immersive theatre · projection · nightlife",
    previewPath: "/experiences",
    defaultSeoTitle: "Experiences · Temple of Kulture",
  }),
  makePageSchema({
    key: "community",
    label: "Community",
    hint: "Family days · heritage · cultural moments",
    previewPath: "/community",
    defaultSeoTitle: "Community · Temple of Kulture",
  }),
  makePageSchema({
    key: "corporate",
    label: "Corporate",
    hint: "Brand partnerships · activations",
    previewPath: "/corporate",
    defaultSeoTitle: "Corporate · Temple of Kulture",
  }),
  makePageSchema({
    key: "whats-on",
    label: "What's On",
    hint: "Listings index. Events live in the Events tab.",
    previewPath: "/whats-on",
    defaultSeoTitle: "What's On · Temple of Kulture",
  }),
  makePageSchema({
    key: "xr-studio",
    label: "XR Studio",
    hint: "Commissions · XR theatre · virtual production",
    previewPath: "/xr-studio",
    defaultSeoTitle: "XR Studio · Temple of Kulture",
  }),
  makePageSchema({
    key: "festivals",
    label: "Festivals",
    hint: "TOK festivals + Hip Hop festival",
    previewPath: "/festivals",
    defaultSeoTitle: "Festivals · Temple of Kulture",
  }),
  makePageSchema({
    key: "level-up",
    label: "LEVEL UP",
    hint: "16–25 immersive academy",
    previewPath: "/level-up-xr",
    defaultSeoTitle: "LEVEL UP · Temple of Kulture",
  }),
  makePageSchema({
    key: "interactive-playground",
    label: "Interactive Playground",
    hint: "Under-12 sensory-aware play",
    previewPath: "/interactive-playground",
    defaultSeoTitle: "Interactive Playground · Temple of Kulture",
  }),
  makePageSchema({
    key: "ubuntu-retreat",
    label: "Ubuntu Retreat",
    hint: "Monthly wellbeing ceremony",
    previewPath: "/ubuntu-retreat",
    defaultSeoTitle: "Ubuntu Retreat · Temple of Kulture",
  }),
];

export const PAGE_SCHEMAS = [HOMEPAGE, ...PHASE3];
export const PAGE_SCHEMA_BY_KEY = Object.fromEntries(
  PAGE_SCHEMAS.map((s) => [s.key, s]),
);
